import { Node, mergeAttributes } from '@tiptap/core';

/**
 * YouTube Embed Extension for TipTap
 */
export const YouTubeEmbed = Node.create({
  name: 'youtube',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      videoId: { default: null },
      width: { default: '100%' },
      height: { default: 315 },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-youtube-video]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const videoId = HTMLAttributes.videoId;
    return [
      'div',
      mergeAttributes({ 
        'data-youtube-video': videoId,
        class: 'youtube-embed',
      }),
      [
        'iframe',
        {
          src: `https://www.youtube.com/embed/${videoId}`,
          width: HTMLAttributes.width,
          height: HTMLAttributes.height,
          frameborder: '0',
          allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
          allowfullscreen: 'true',
        },
      ],
    ];
  },

  addCommands() {
    return {
      setYouTubeVideo: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

/**
 * Twitter/X Embed Extension for TipTap
 */
export const TwitterEmbed = Node.create({
  name: 'twitter',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      tweetId: { default: null },
      tweetUrl: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-twitter-embed]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({
        'data-twitter-embed': HTMLAttributes.tweetId,
        class: 'twitter-embed',
      }),
      [
        'blockquote',
        { class: 'twitter-tweet' },
        [
          'a',
          { href: HTMLAttributes.tweetUrl },
          'Loading tweet...',
        ],
      ],
    ];
  },

  addCommands() {
    return {
      setTwitterEmbed: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

/**
 * Generic Link Preview/Embed Extension
 */
export const LinkEmbed = Node.create({
  name: 'linkEmbed',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      url: { default: null },
      title: { default: null },
      description: { default: null },
      image: { default: null },
      domain: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-link-embed]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes({
        'data-link-embed': HTMLAttributes.url,
        href: HTMLAttributes.url,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'link-embed',
      }),
      [
        'div',
        { class: 'link-embed-content' },
        [
          'div',
          { class: 'link-embed-text' },
          ['div', { class: 'link-embed-title' }, HTMLAttributes.title || HTMLAttributes.url],
          HTMLAttributes.description 
            ? ['div', { class: 'link-embed-description' }, HTMLAttributes.description]
            : '',
          ['div', { class: 'link-embed-domain' }, HTMLAttributes.domain || new URL(HTMLAttributes.url).hostname],
        ],
      ],
    ];
  },

  addCommands() {
    return {
      setLinkEmbed: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

/**
 * Spotify Embed Extension
 */
export const SpotifyEmbed = Node.create({
  name: 'spotify',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      spotifyUri: { default: null },
      type: { default: 'track' }, // track, album, playlist, episode
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-spotify-embed]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const uri = HTMLAttributes.spotifyUri;
    return [
      'div',
      mergeAttributes({
        'data-spotify-embed': uri,
        class: 'spotify-embed',
      }),
      [
        'iframe',
        {
          src: `https://open.spotify.com/embed/${HTMLAttributes.type}/${uri}`,
          width: '100%',
          height: HTMLAttributes.type === 'track' ? 80 : 380,
          frameborder: '0',
          allow: 'encrypted-media',
        },
      ],
    ];
  },

  addCommands() {
    return {
      setSpotifyEmbed: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

/**
 * Helper to parse URLs and detect embed type
 */
export function parseEmbedUrl(url) {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // YouTube
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      let videoId = null;
      if (hostname.includes('youtu.be')) {
        videoId = urlObj.pathname.slice(1);
      } else {
        videoId = urlObj.searchParams.get('v');
      }
      if (videoId) {
        return { type: 'youtube', videoId, url };
      }
    }

    // Twitter/X
    if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
      const match = urlObj.pathname.match(/\/status\/(\d+)/);
      if (match) {
        return { type: 'twitter', tweetId: match[1], tweetUrl: url };
      }
    }

    // Spotify
    if (hostname.includes('spotify.com')) {
      const match = urlObj.pathname.match(/\/(track|album|playlist|episode)\/([a-zA-Z0-9]+)/);
      if (match) {
        return { type: 'spotify', spotifyType: match[1], spotifyUri: match[2], url };
      }
    }

    // Vimeo
    if (hostname.includes('vimeo.com')) {
      const match = urlObj.pathname.match(/\/(\d+)/);
      if (match) {
        return { type: 'vimeo', videoId: match[1], url };
      }
    }

    // Generic link
    return { type: 'link', url, domain: hostname };
  } catch {
    return null;
  }
}

export default { YouTubeEmbed, TwitterEmbed, LinkEmbed, SpotifyEmbed, parseEmbedUrl };
